/* eslint-disable no-console */
import {
 all, call, put, select,
} from 'redux-saga/effects';
import queryString from 'query-string';
import chroma from 'chroma-js';
import { API, getServerConfig } from '../../api';

const getAccordian = ({ accordian }) => accordian;

const THEMES = {
  default: ['#8833a3', '#3f72e8', '#02a32f', '#b7d012', '#e8b03f', '#e85b3f'],
  'category-range': ['#9fe1ac', '#e4a95b', '#a83b4b'],
  'tricolor-1': ['#a83b4b', '#e4a95b', '#9fe1ac'],
  'tricolor-2': ['#9fe1ac', '#e4a95b', '#a83b4b'],
  'tricolor-3': ['#9fe1ac', '#FF7477', '#a83b4b'],
  'tricolor-4': ['#691b51', '#9cf6f6', '#00975e'],
  'positive-1': ['#c2e699', '#78c679', '#31a354', '#006837', '#044215'],
  'positive-1-inverse': ['#044215', '#31a354', '#c2e699'],
  'positive-2': [
    '#c9deff',
    '#b3d1ff',
    '#80b3ff',
    '#1a75ff',
    '#0047b3',
    '#002966',
  ],
  'positive-3': [
    '#ece7f8',
    '#DACEF3',
    '#BFA9EF',
    '#8860E6',
    '#6029E0',
    '#39148F',
  ],
  'negative-1': ['#ff6767', '#e60000', '#9b0000'],
  'negative-2': ['#ffc799', '#ff8000', '#b35a00'],
  'negative-3': ['#fff0b4', '#ffe067', '#cea300'],
  'negative-4': ['#ffe067', '#ff8000', '#9b0000'],
  'negative-4-inverse': ['#9b0000', '#ff8000', '#ffe067'],
  'neutral-1': ['#efefef', '#bbb', '#888'],
};

export const makeStyle = (layer, values) => {
  let newStyle = {};
  if (!values || !values.length) return layer;

  const paintAdditions = {
    'circle-radius': layer.style === 'circle' ? 4 : undefined,
    'line-dasharray': layer.dasharray,
    'line-width': layer.lineWidth ? layer.lineWidth : undefined,
  };

  Object.keys(paintAdditions).forEach((key) => {
    if (paintAdditions[key] == null) delete paintAdditions[key];
  });

  if (layer.type === 'RANGE' || layer.type === 'DATA_STREAM_RANGE') {
    const valuesWithInfinity = Array.from(
      new Set([...(values || layer.values), Infinity]),
    );

    const colors = chroma
      .scale(THEMES[layer.colorScheme] || THEMES.default)
      .mode('lch')
      .colors(valuesWithInfinity.length - 1);

    const stops = valuesWithInfinity.map((value, i) => [
      value,
      colors[i] || colors[colors.length - 1],
    ]);

    newStyle = {
      type: `${layer.style}`,
      opacity: layer.opacity,
      paint: {
        ...paintAdditions,
        [`${layer.style}-color`]: {
          property: layer.key.split('+')[0],
          default: 'transparent',
          stops,
        },
      },
    };
  }

  if (layer.type === 'CATEGORY_RANGE' || layer.type === 'DATA_STREAM_CATEGORY_RANGE') {
    const colors = chroma
      .scale(THEMES[layer.colorScheme] || THEMES['category-range'])
      .mode('lch')
      .colors(values.length);

    newStyle = {
      type: `${layer.style}`,
      paint: {
        ...paintAdditions,
        [`${layer.style}-color`]: {
          type: 'categorical',
          property: layer.key.split('+')[0],
          default: 'transparent',
          stops: values.map((value, i) => [value, colors[i]]),
        },
      },
    };
  }

  if (layer.type === 'CATEGORY' || layer.type === 'DATA_STREAM_CATEGORY') {
    const colors = chroma
      .scale(THEMES[layer.colorScheme] || THEMES.default)
      .mode('lch')
      .colors(values.length);

    newStyle = {
      type: `${layer.style}`,
      paint: {
        ...paintAdditions,
        [`${layer.style}-color`]: {
          type: 'categorical',
          property: layer.key.split('+')[0],
          default: 'transparent',
          stops: values
            .filter(Boolean)
            .sort()
            .map((value, i) => [value, colors[i]]),
        },
      },
    };
  }
  return {
    ...layer,
    style: newStyle,
  };
};

export function* fetchLayer(layer) {
  const serverConfig = yield select(getServerConfig);
  const {
    type,
    source,
    key,
    layerOptionsKey,
  } = layer;

  if (!type || layer.locked) return layer;

  if (layer.type === 'COMPOSITE_LAYER') {
    // eslint-disable-next-line no-param-reassign
    layer.layers = yield all(
      layer.layers.map((innerLayer) => call(fetchLayer, innerLayer)).reverse(),
    );
    return layer;
  }

  if (layer.values && layer.values.length) {
    return makeStyle(layer, layer.values);
  }

  try {
    const query = {
      key: (layerOptionsKey || key).split('+')[0],
    };

    if (source) query.source = source;

    const layerFilterString =
      layer.layerOptionsFilterString || layer.filterString;

    const path = `/layer-options/${
      serverConfig.namespace
    }/${type.toLowerCase()}?${queryString.stringify(query)}${
      layerFilterString ? `&${layerFilterString}` : ''
    }`;
    const result = yield call(API, 'get', 'api', path);
    const newLayer = makeStyle(layer, result.values);

    return newLayer;
  } catch (e) {
    console.error(e);
  }
  return layer;
}

export function* fetchSection(section) {
  const layers = yield all(
    section.layers.map((layer) => call(fetchLayer, layer)),
  );

  return {
    ...section,
    layers,
  };
}

export function* fetchLayers() {
  const accordian = yield select(getAccordian);

  yield put({ type: 'LAYERS_OPTIONS_LOADING' });

  try {
    const newAccordian = yield all(
      accordian.map((section) => call(fetchSection, section)),
    );

    yield put({
      type: 'SET_ACCORDIAN',
      payload: {
        accordian: newAccordian,
      },
    });
    yield put({
      type: 'SELECT_DEFAULT_LAYERS',
    });
  } catch (e) {
    console.error(e);
  }

  yield put({ type: 'LAYERS_OPTIONS_LOADED' });
}
